

import React, { useState, useEffect, useMemo, useRef, } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { Alert, Form, Button, Checkbox, } from 'antd';

import { API_LOGIN_URL, API_USER_URL, EMAIL_RE } from '../utils';
import { api } from '../api/client';
import { signInDevice } from '../services/deviceSessions';
import { setSessionToken } from '../utils/session';
import { apiErrorMessage } from '../utils/apiErrors';
import { InputPassword, InputText } from '../components/Input';
import TurnstileWidget, { turnstileEnabled } from '../components/common/TurnstileWidget';
import { BrandHero } from '../components/brand/Brand';
import { AdminJSON } from '../utils/admin';
import { BAN_CIRCLE, CHECK_CIRCLE_FILL } from '../components/icons';
import LegalLinks from '../components/legal/LegalLinks';

// A person takes longer than this to fill four fields. A submit faster than
// it is a form-filler; the API's honeypot and challenge catch the rest.
const MIN_FILL_MS = 2000;
const NAME_MAX = 60;

// The honeypot: a field no person sees or tabs into. Anything typed into it
// refuses the registration server-side; the form also declines to send.
const HONEYPOT_STYLE = { position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden', opacity: 0 };




/**
 * Keep the session a new account starts with. A browser keeps the token the
 * registration answered (the web's 4-week token). The phone app signs in
 * again with its `device` (KPR-1674): registration starts no device session,
 * and only a device session stays signed in for months. Should that sign-in
 * fail, the registration's own token is kept, so the person is never left
 * signed out of the account they just made.
 */
async function keepSession( registrationToken, { email, password } ) {
	const onDevice = signInDevice();
	if ( onDevice.device ) {
		try {
			const { data } = await api.post( API_LOGIN_URL, { username: email, password, ...onDevice } );
			if ( data?.token && data?.refreshToken ) {
				setSessionToken( data.token, { refreshToken: data.refreshToken } );
				return;
			}
		} catch ( _ ) { /* keep the registration's token below */ }
	}
	setSessionToken( registrationToken );
}

export default function Register() {

	const navigate = useNavigate();

	const [inputs, setInputs] = useState({
		email: '',
		password: '',
		confirmPassword: '',
		firstName: '',
		lastName: '',
		website: '',
		// "I'm 16 or older" (KPR-477, ruling p02). Never pre-ticked: the
		// person says it, and the API refuses a sign-up without it.
		ageConfirmed: false,
	});

	const [ emailIsValid, setEmailIsValid ] = useState( false );

	const [ passwordIsValid, setPasswordIsValid ] = useState( false );

	const [ confirmPasswordIsValid, setConfirmPasswordIsValid ] = useState( false );

	const [ errorText, setErrorText ] = useState( null );
	const [ submitting, setSubmitting ] = useState( false );
	const [ turnstileToken, setTurnstileToken ] = useState( null );
	const [ turnstileReset, setTurnstileReset ] = useState( 0 );

	const mountedAt = useRef( Date.now() );



	const handleChange = ( eName, eValue ) => setInputs( prevState => ( { ...prevState, [ eName ]: eValue } ) );



	const handleSubmit = async () => {
		if ( Date.now() - mountedAt.current < MIN_FILL_MS || inputs.website ) {
			// Too fast for hands, or the hidden field is filled: not a person.
			setErrorText( 'Please take a moment and try again.' );
			return;
		}

		// Only send the fields the server expects (per Joi schema)
		const { email, password, firstName, lastName, website, ageConfirmed } = inputs;
		const payload = {
			// Any case is accepted; the address is sent (and stored) in lowercase.
			email: email.trim().toLowerCase(),
			password,
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			website,
			ageConfirmed: ageConfirmed === true,
			...( turnstileToken ? { turnstileToken } : {} ),
		};

		setSubmitting( true );
		setErrorText( null );
		try {
			const response = await fetch( API_USER_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( payload ),
			} );

			if ( response.status !== 201 ) {
				let body = null;
				try {
					body = await response.json();
				} catch ( _ ) {}
				// The API's refusals carry a code and a sentence — same reader
				// the axios paths use, over a fetch-shaped error.
				const retryAfter = response.headers.get( 'retry-after' );
				setErrorText( apiErrorMessage(
					{ response: { status: response.status, data: body || {}, headers: retryAfter ? { 'retry-after': retryAfter } : {} } },
					response.status === 409 ? 'An account with that email address already exists.' : 'Registration could not be completed.'
				) );
				// A challenge token is single-use: get a fresh one for the retry.
				setTurnstileReset( key => key + 1 );
				return;
			}

			const data = await response.json();

			// Save the token and land in the personal collection the server
			// made for the account (its welcome note and first to dos are the
			// first-run guide). An API a deploy behind sends no id: the
			// collections page is the next best place — never the profile.
			if ( data && data.token ) {
				await keepSession( data.token, payload );
				navigate( data.personalCollectionId ? `/collections/${ data.personalCollectionId }` : '/collections' );
			}
		} catch ( err ) {
			setErrorText( "We couldn't reach the server. Check your connection and try again." );
		} finally {
			setSubmitting( false );
		}
	};



	const validateForm = () => {
		return inputs.firstName.trim().length > 0
			&& inputs.firstName.length <= NAME_MAX
			&& inputs.lastName.trim().length > 0
			&& inputs.lastName.length <= NAME_MAX
			&& emailIsValid
			&& passwordIsValid
			&& confirmPasswordIsValid
			&& inputs.ageConfirmed === true;
	};



	return ( <div className='auth-page'>
		<BrandHero />
		<h1>Create your account</h1>

		<span>Already have an account? <Link to='/login'>Sign in</Link></span>

		<Form onSubmit={( e ) => e.preventDefault()}>
			{/* Explicit tokens: InputText now defaults autofill OFF (element
			    fields are about items, not the visitor), and these two really
			    are the visitor's own name. */}
			<InputText
				elementName='firstName'
				elementLabel='First name'
				value={inputs.firstName}
				onChange={handleChange}
				autoComplete='given-name'
			/>
			<InputText
				elementName='lastName'
				elementLabel='Last name'
				value={inputs.lastName}
				onChange={handleChange}
				autoComplete='family-name'
			/>

			<InputEmailWrapper
				elementName='email'
				elementLabel='Email'
				value={inputs.email}
				onChange={handleChange}
				setEmailIsValid={setEmailIsValid}
				autoComplete='username'
			/>

			<InputPasswordWrapper
				elementName='password'
				elementLabel='Password'
				value={inputs.password}
				onChange={handleChange}
				setPasswordIsValid={setPasswordIsValid}
				autoComplete='new-password'
			/>

			<InputConfirmPasswordWrapper
				elementName='confirmPassword'
				elementLabel='Confirm password'
				value={inputs.confirmPassword}
				onChange={handleChange}
				setConfirmPasswordIsValid={setConfirmPasswordIsValid}
				compareValue={inputs.password}
				autoComplete='confirm-password'
			/>

			{/* Where the terms acceptance will sit beside it once the terms are
			    published; until then the age line stands alone. */}
			<Form.Item style={{ marginBottom: 12 }}>
				<Checkbox
					checked={inputs.ageConfirmed}
					onChange={e => handleChange( 'ageConfirmed', e.target.checked )}
				>
					I&apos;m 16 or older
				</Checkbox>
			</Form.Item>

			{/* The honeypot. Off-screen, out of the tab order, ignored by autofill. */}
			<div style={HONEYPOT_STYLE} aria-hidden='true'>
				<label htmlFor='register-website'>Website</label>
				<input
					id='register-website'
					name='website'
					type='text'
					tabIndex={-1}
					autoComplete='off'
					value={inputs.website}
					onChange={e => handleChange( 'website', e.target.value )}
				/>
			</div>

			{turnstileEnabled() ? (
				<TurnstileWidget action='register' onToken={setTurnstileToken} resetKey={turnstileReset} />
			) : null}

			{errorText ? (
				<Alert type='error' showIcon message={errorText} style={{ marginBottom: 12 }} />
			) : null}

			<Button onClick={handleSubmit} disabled={! validateForm()} loading={submitting}>Sign up</Button>
		</Form>



		<AdminJSON obj={inputs} label={'inputs'} />

		<LegalLinks />

	</div> );

}



export const InputConfirmPasswordWrapper = ( props ) => {

	const {
		elementName,
		elementLabel,
		value,
		onChange,
		compareValue,
		setConfirmPasswordIsValid,
		autoComplete,
	} = props;



	const isValid = useMemo( () => {
		return compareValue === value && value.length > 0;
	}, [ value, compareValue, ] );



	useEffect( () => {
		setConfirmPasswordIsValid( isValid );
	}, [ setConfirmPasswordIsValid, isValid, ] );



	return <React.Fragment>
		<InputPassword
			elementName={elementName}
			elementLabel={elementLabel}
			value={value}
			onChange={onChange}
			autoComplete={autoComplete}
			// {...props}
		/>

		<div>
			<span className={'d-block ' + ( isValid ? 'text-success' : 'text-secondary')}>
				<span className='mx-1'>
					{isValid ? <CHECK_CIRCLE_FILL /> : <BAN_CIRCLE />}
				</span>

				{'Must match first password'}
			</span>
		</div>
	</React.Fragment>;

}



export const InputEmailWrapper = ( { elementName, elementLabel, value, onChange, setEmailIsValid, autoComplete, } ) => {

	const isValid = useMemo( () => {
		const isValid = EMAIL_RE.test( value );

		// TODO - check for existing account with that address ?

		return isValid;
	}, [ value ] );



	useEffect( () => {
		setEmailIsValid( isValid );
	}, [ setEmailIsValid, isValid, ] );



	return <>
		<InputText
			elementName={elementName}
			elementLabel={elementLabel}
			value={value}
			onChange={onChange}
			autoComplete={autoComplete}
			/>

		<div>
			<span className={'d-block ' + ( isValid ? 'text-success' : 'text-secondary')}>
				<span className='mx-1'>
					{isValid ? <CHECK_CIRCLE_FILL /> : <BAN_CIRCLE />}
				</span>

				{'Must be a valid email format'}
			</span>
		</div>
	</>;

}




export const InputPasswordWrapper = ( { elementName, elementLabel, value, onChange, setPasswordIsValid, autoComplete, } ) => {

	const rules = useMemo( () => [
		{
			label: 'Must be at least 8 characters',
			validate: ( value ) => {
				return value.length >= 8;
			},
		},
		{
			label: 'Must contain a lowercase letter',
			validate: ( value ) => {
				return /[a-z]/.test( value );
			},
		},
		{
			label: 'Must contain an uppercase letter',
			validate: ( value ) => {
				return /[A-Z]/.test( value );
			},
		},
		{
			label: 'Must contain a number',
			validate: ( value ) => {
				return /[0-9]/.test( value );
			},
		},
		{
			label: 'Must contain a special character',
			validate: ( value ) => {
				return /[!@#$%&.,/\\_()^*[\]{}-]/.test( value );
			},
		},
	], [] );



	useEffect( () => {

		if ( ! setPasswordIsValid ) {
			return;
		}

		let allRulesPassed = true;

		for ( let idx = 0; idx < rules.length; ++idx ) {
			if ( ! rules[ idx ].validate( value ) ) {
				allRulesPassed = false;
				break;
			}
		}

		setPasswordIsValid( allRulesPassed );

	}, [ value, rules, setPasswordIsValid, ] );



	return <>
			<InputPassword
				elementName={elementName}
				elementLabel={elementLabel}
				value={value}
				onChange={onChange}
				autoComplete={autoComplete}
				/>

			<div>
				{rules.map( rule => {
					const isValid = rule.validate( value );

					return <span
						key={rule.label}
						className={'d-block ' + ( isValid ? 'text-success' : 'text-secondary' )}
					>
						<span className='mx-1'>
							{isValid ? <CHECK_CIRCLE_FILL /> : <BAN_CIRCLE />}
						</span>

						{rule.label}
					</span>
				} )}
			</div>

	</>;

}
